import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import GlobeScene from "@/components/three/GlobeScene";
import { nodeStore } from "@/components/three/Globe";
import { CATEGORIES } from "@/lib/data";
import { CONFIG } from "@/lib/config";
import { EASE, Mono, Btn, scrollToId } from "@/components/bits";

const REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const D = REDUCED ? 0 : 2.45;
const MOBILE_CHIPS = ["REAL ESTATE", "PRIVATE CREDIT", "TRADE FINANCE", "TREASURY"];

function HeadLine({ children, delay, className = "" }) {
    return (
        <span className="block overflow-hidden pb-1">
            <motion.span
                className={`block ${className}`}
                initial={REDUCED ? false : { y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.05, delay, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

function ParallaxLogo({ sx, sy }) {
    const x = useTransform(sx, (v) => v * 22);
    const y = useTransform(sy, (v) => v * 14);
    return (
        <motion.img
            src={CONFIG.LOGO}
            alt="VERA logo"
            className="absolute left-1/2 top-[45%] -translate-x-1/2 -translate-y-1/2 w-44 xl:w-56 object-contain mask-fade-circle pointer-events-none select-none drop-shadow-[0_0_40px_rgba(3,7,17,0.9)]"
            style={{ x, y }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: D + 0.35, duration: 1 }}
            data-testid="hero-logo-overlay"
        />
    );
}

function TrackedOverlay({ containerRef, onSelect, selectedId, featuredId }) {
    const els = useRef([]);

    useEffect(() => {
        let raf;
        const loop = () => {
            const wrap = containerRef.current;
            if (wrap) {
                const rect = wrap.getBoundingClientRect();
                if (rect.width > 0) {
                    const cx = rect.width / 2;
                    const cy = rect.height / 2;
                    CATEGORIES.forEach((c, i) => {
                        const el = els.current[i];
                        const s = nodeStore.screen.find((p) => p && p.id === c.id);
                        if (!el || !s) return;
                        const x = s.nx * rect.width;
                        const y = s.ny * rect.height;
                        const active = selectedId === c.id || featuredId === c.id;
                        const vis = s.front > 0.04;
                        el.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
                        el.style.opacity = vis ? String(0.3 + s.front * 0.7) : "0";
                        el.style.pointerEvents = vis && s.front > 0.45 ? "auto" : "none";
                        el.classList.toggle("is-active", active);
                        const dx = x - cx;
                        const dy = y - cy;
                        const ang = dx === 0 && dy === 0 ? 0.6 : Math.atan2(dy, dx);
                        const dist = 46 + (i % 3) * 12;
                        const line = el.querySelector(".track-line");
                        const label = el.querySelector(".track-label");
                        if (line) {
                            line.style.width = `${dist}px`;
                            line.style.transform = `rotate(${ang}rad)`;
                        }
                        if (label) {
                            let lx = Math.cos(ang) * dist;
                            const ly0 = Math.sin(ang) * dist;
                            const absX = rect.left + x + lx;
                            const vw = window.innerWidth;
                            if (absX > vw - 90) lx -= absX - (vw - 90);
                            if (absX < 90) lx += 90 - absX;
                            const ly = Math.max(30 - y, Math.min(rect.height - 30 - y, ly0));
                            label.style.transform = `translate(${lx.toFixed(1)}px, ${ly.toFixed(1)}px) translate(-50%, -50%)`;
                        }
                    });
                }
            }
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
    }, [containerRef, selectedId, featuredId]);

    return (
        <div className="absolute inset-0 z-[5]">
            {CATEGORIES.map((c, i) => (
                <button
                    type="button"
                    key={c.id}
                    ref={(el) => (els.current[i] = el)}
                    className="track-item"
                    data-testid={`globe-track-${c.id}`}
                    onMouseEnter={() => {
                        nodeStore.hoveredId = c.id;
                    }}
                    onMouseLeave={() => {
                        if (nodeStore.hoveredId === c.id) nodeStore.hoveredId = null;
                    }}
                    onClick={() => onSelect(c.id)}
                >
                    <span className="track-dot" aria-hidden="true" />
                    <span className="track-line" aria-hidden="true" />
                    <span className="track-label">{c.label}</span>
                </button>
            ))}
        </div>
    );
}

function CoordHud({ containerRef }) {
    const ref = useRef(null);
    useEffect(() => {
        const f = (e) => {
            const el = containerRef.current;
            const box = ref.current;
            if (!el || !box) return;
            const r = el.getBoundingClientRect();
            if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) return;
            const dx = (e.clientX - (r.left + r.width / 2)) / (r.width * 0.29);
            const dy = (e.clientY - (r.top + r.height / 2)) / (r.height * 0.29);
            const inside = dx * dx + dy * dy <= 1.05;
            const lat = Math.max(-90, Math.min(90, -dy * 78));
            const lon = Math.max(-180, Math.min(180, dx * 155));
            const fmt = (v, p, n) => `${Math.abs(v).toFixed(1)}° ${v >= 0 ? p : n}`;
            box.textContent = `${inside ? "LOCK" : "SCAN"} · LAT ${fmt(lat, "N", "S")} · LON ${fmt(lon, "E", "W")}`;
        };
        window.addEventListener("mousemove", f, { passive: true });
        return () => window.removeEventListener("mousemove", f);
    }, [containerRef]);
    return (
        <div
            ref={ref}
            data-testid="globe-coord-hud"
            className="absolute left-5 bottom-5 z-10 font-mono text-[9px] tracking-[0.22em] text-vera-soft/80 border border-white/10 bg-vera-bg/70 backdrop-blur px-3 py-2 select-none"
        >
            SCAN · LAT 00.0° N · LON 000.0° E
        </div>
    );
}

function NodePanel({ selected, onClose, className = "" }) {
    if (!selected) return null;
    return (
        <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 18 }}
            transition={{ duration: 0.4, ease: EASE }}
            className={`glass-card p-6 z-20 ${className}`}
            data-testid={`globe-node-panel-${selected.id}`}
        >
            <div className="flex items-center justify-between mb-4">
                <Mono className="text-vera-gold2">{selected.label}</Mono>
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="globe-panel-back-button"
                    className="font-mono text-[10px] tracking-[0.25em] border border-white/15 px-3.5 py-1.5 text-vera-mut hover:text-white hover:border-vera-soft/60 transition-all"
                >
                    ← BACK
                </button>
            </div>
            <p className="text-sm text-vera-mut leading-relaxed mb-5">{selected.desc}</p>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
                {selected.flow.map((step, i) => (
                    <span key={step} className="flex items-center gap-2">
                        {i > 0 && <span className="text-vera-mut/50 font-mono text-[9px]">→</span>}
                        <span className={`font-mono text-[9px] tracking-[0.18em] px-2.5 py-1.5 border ${step === "CAPITAL" ? "border-vera-gold/60 text-vera-gold2 bg-vera-gold/10" : "border-vera-blue/40 text-[#A8D2FF] bg-vera-blue/5"}`}>
                            {step}
                        </span>
                    </span>
                ))}
            </div>
            <p className="font-mono text-[9px] tracking-[0.2em] text-vera-mut/60 mt-5">INTERACTIVE CONCEPT — DEMO, NO LIVE ASSET DATA</p>
        </motion.div>
    );
}

const Corner = ({ pos }) => <div aria-hidden="true" className={`hidden lg:block absolute w-5 h-5 border-vera-blue/50 z-30 pointer-events-none ${pos}`} />;

export default function Hero() {
    const [selectedId, setSelectedId] = useState(null);
    const [featuredId, setFeaturedId] = useState(CATEGORIES[0].id);
    const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
    const globeWrap = useRef(null);
    const lastInteraction = useRef(0);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 45, damping: 18 });
    const sy = useSpring(my, { stiffness: 45, damping: 18 });
    const reduced = useReducedMotion();
    const selected = CATEGORIES.find((c) => c.id === selectedId) || null;

    useEffect(() => {
        const mq = window.matchMedia("(min-width: 1024px)");
        const f = () => setIsDesktop(mq.matches);
        mq.addEventListener("change", f);
        return () => mq.removeEventListener("change", f);
    }, []);

    useEffect(() => {
        const iv = setInterval(() => {
            if (document.hidden) return;
            if (Date.now() - lastInteraction.current < 6500) return;
            setFeaturedId((prev) => {
                const idx = CATEGORIES.findIndex((c) => c.id === prev);
                return CATEGORIES[(idx + 1) % CATEGORIES.length].id;
            });
        }, 3600);
        return () => clearInterval(iv);
    }, []);

    useEffect(() => {
        if (selectedId) lastInteraction.current = Date.now();
    }, [selectedId]);

    const onMove = (e) => {
        if (reduced) return;
        mx.set((e.clientX / window.innerWidth) * 2 - 1);
        my.set((e.clientY / window.innerHeight) * 2 - 1);
    };

    const select = (id) => {
        setSelectedId(id);
        lastInteraction.current = Date.now();
    };

    const globeProps = { selectedId, featuredId, onSelect: select };

    return (
        <section className="relative min-h-screen flex flex-col overflow-hidden" onMouseMove={onMove} data-testid="hero-section">
            <div className="absolute inset-0 vera-grid-bg" aria-hidden="true" />
            <div className="absolute top-[-10%] right-[-15%] w-[70vw] h-[70vw] max-w-[900px] max-h-[900px] rounded-full bg-vera-blue/10 blur-[130px]" aria-hidden="true" />
            <div className="absolute bottom-[-30%] left-[-10%] w-[50vw] h-[50vw] rounded-full bg-vera-gold/5 blur-[120px]" aria-hidden="true" />

            <Corner pos="top-24 left-5 border-t border-l" />
            <Corner pos="top-24 right-5 border-t border-r" />
            <Corner pos="bottom-16 left-5 border-b border-l" />
            <Corner pos="bottom-16 right-5 border-b border-r" />

            {isDesktop && (
                <div ref={globeWrap} className="absolute right-[-9%] top-[3%] w-[57vw] h-[86vh] z-0" data-testid="hero-3d-globe-container">
                    <GlobeScene {...globeProps} />
                    <TrackedOverlay containerRef={globeWrap} onSelect={select} selectedId={selectedId} featuredId={featuredId} />
                    <CoordHud containerRef={globeWrap} />
                    <ParallaxLogo sx={sx} sy={sy} />
                    <AnimatePresence>
                        {selected && <NodePanel selected={selected} onClose={() => setSelectedId(null)} className="absolute bottom-8 left-8 w-[380px]" />}
                    </AnimatePresence>
                </div>
            )}

            <div className={`relative z-20 w-full max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 flex-1 flex items-center ${isDesktop ? "pt-28 pb-20" : "pt-28 pb-10"}`}>
                <div className="max-w-2xl">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: D, duration: 0.7 }}
                        className="flex items-center gap-3 mb-8"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-vera-gold animate-pulse-soft" aria-hidden="true" />
                        <Mono className="text-vera-gold2">VERIFIED REAL ASSETS</Mono>
                    </motion.div>

                    <h1 className="text-[2.6rem] sm:text-6xl xl:text-[4.2rem] font-black tracking-tight leading-[1.02] mb-7">
                        <HeadLine delay={D + 0.1}>REAL ASSETS.</HeadLine>
                        <HeadLine delay={D + 0.22} className="text-gold-grad">PROGRAMMABLE</HeadLine>
                        <HeadLine delay={D + 0.3} className="text-gold-grad">CAPITAL.</HeadLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: D + 0.4, duration: 0.8 }}
                        className="text-vera-mut text-sm sm:text-base leading-relaxed max-w-lg mb-10"
                    >
                        VERA transforms verified real-world assets and economic cash flows into programmable onchain capital, with USDC settlement on Arc.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: D + 0.52, duration: 0.8 }}
                        className="flex flex-wrap items-center gap-4"
                    >
                        <Btn variant="blue" testid="hero-cta-explore" onClick={() => scrollToId("capital")}>
                            EXPLORE VERA
                        </Btn>
                        <Btn variant="ghost" to="/article" testid="hero-cta-thesis">
                            READ THE THESIS
                        </Btn>
                        <Btn variant="gold" to="/proof" testid="hero-cta-proof" className="!px-5 !py-3">
                            VIEW PROOF
                        </Btn>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: D + 0.8, duration: 0.8 }}
                        className="flex items-center gap-3 mt-12"
                        data-testid="hero-node-hint"
                    >
                        <span className="w-2 h-2 rotate-45 border border-vera-gold animate-pulse-soft" aria-hidden="true" />
                        <Mono className="text-vera-mut">SELECT A NODE TO TRACE ITS CAPITAL FLOW</Mono>
                    </motion.div>
                </div>
            </div>

            {!isDesktop && (
                <div className="relative z-20 px-5 pb-6">
                    <div className="relative h-[44vh] min-h-[300px]" data-testid="hero-3d-globe-container">
                        <GlobeScene {...globeProps} />
                    </div>
                    <div className="flex gap-2 overflow-x-auto mt-2 pb-2">
                        {MOBILE_CHIPS.map((label) => {
                            const c = CATEGORIES.find((x) => x.label === label);
                            return (
                                <button
                                    key={label}
                                    type="button"
                                    onClick={() => select(c.id)}
                                    data-testid={`hero-chip-${c.id}`}
                                    className={`shrink-0 glass-card px-4 py-2.5 flex items-center gap-2.5 font-mono text-[9px] tracking-[0.2em] ${selectedId === c.id ? "!border-vera-gold/60 text-vera-gold2" : "text-vera-white/85"}`}
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-vera-gold shrink-0" aria-hidden="true" />
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                    <AnimatePresence>
                        {selected && <NodePanel selected={selected} onClose={() => setSelectedId(null)} className="mt-2" />}
                    </AnimatePresence>
                </div>
            )}

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: D + 0.9, duration: 1 }}
                className="relative z-20 border-t border-white/8 bg-vera-bg/60 backdrop-blur"
                data-testid="hero-bottom-strip"
            >
                <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between gap-4">
                    {["BUILT FOR ARC", "USDC SETTLEMENT", "REAL-WORLD ASSETS", "PROGRAMMABLE CAPITAL"].map((t, i) => (
                        <span key={t} className={`flex items-center gap-3 ${i > 1 ? "hidden md:flex" : ""}`}>
                            <Mono className="text-vera-mut">{t}</Mono>
                            {i < 3 && <span className="w-1 h-1 rotate-45 bg-vera-gold/70 hidden md:block" aria-hidden="true" />}
                        </span>
                    ))}
                    <span className="md:hidden flex items-center gap-2 text-vera-mut">
                        <Mono className="text-vera-mut">SCROLL</Mono>
                        <span className="w-3 h-3 border-b border-r border-vera-mut rotate-45 animate-bounce" aria-hidden="true" />
                    </span>
                </div>
            </motion.div>
        </section>
    );
}
